import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Label } from "@/components/motion";
import { DESTINATIONS, IMAGES, useTitle } from "@/data/site";

function EditorialCard({ d, reverse }) {
  return (
    <Reveal>
      <Link
        to={`/destinations/${d.slug}`}
        data-testid={`destinations-card-${d.slug}`}
        className={`group grid items-center gap-10 lg:grid-cols-12`}
      >
        <div className={`overflow-hidden lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
          <img
            src={d.cardImage}
            alt={d.name}
            loading="lazy"
            className="aspect-[16/10] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          />
        </div>
        <div className={`lg:col-span-5 ${reverse ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
          <p className="label text-gold">{d.region}</p>
          <h3 className="mt-4 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">
            {d.name}
          </h3>
          <p className="mt-5 max-w-md text-base leading-relaxed text-graphite">{d.tagline}</p>
          <span className="link-arrow mt-8 text-ink transition-colors group-hover:text-gold">
            Explore {d.name} <span aria-hidden>→</span>
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Destinations() {
  useTitle("Destinations | Urban Trail");
  const domestic = DESTINATIONS.filter((d) => d.region === "Domestic");
  const international = DESTINATIONS.filter((d) => d.region === "International");

  return (
    <div data-testid="destinations-page">
      <PageHero
        image={IMAGES.destinationsHero}
        label="Destinations"
        titleLines={["Discover your", "next journey."]}
        sub="From the high Himalayas to tropical islands and extraordinary cities, explore destinations selected for their character, beauty and experiences."
        tall
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <Reveal>
            <Label>Domestic</Label>
          </Reveal>
          <div className="mt-16 space-y-28">
            {domestic.map((d, i) => (
              <EditorialCard key={d.slug} d={d} reverse={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-mist bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <Reveal>
            <Label>International</Label>
          </Reveal>
          <div className="mt-16 space-y-28">
            {international.map((d, i) => (
              <EditorialCard key={d.slug} d={d} reverse={i % 2 === 1} />
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-28 font-serif text-2xl font-light italic text-steel">
              More destinations coming soon.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand
        image={IMAGES.finalCta}
        title="Can't find your journey?"
        sub="Tell us where you'd like to go — if it belongs on your map, we'll help you experience it beautifully."
        primaryText="Plan Your Journey"
        primaryTo="/contact"
      />
    </div>
  );
}
